import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Button,
    Grid,
    TextField,
    Autocomplete,
    IconButton,
    Typography,
    Box,
    Divider,
    Collapse,
    Alert,
    AlertTitle,
    List,
    ListItem,
    ListItemText,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import MasterCommonTable from "../../UiComponents/CommonTable/MasterCommonTable";
import { GetCompany, PostBulkUploadJob, GetBulkUploadJob } from "../../Service/_index";
import { ToastError } from "../../UiComponents/Toaster/Toast";

const BulkUploadDialog = ({ open, onClose }) => {
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [companyList, setCompanyList] = useState([]);
    const [bulkUploadJobs, setBulkUploadJobs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [expandedRows, setExpandedRows] = useState(new Set());
    const [refreshing, setRefreshing] = useState(false);

    // Allowed companies - only these will show in dropdown
    const allowedCompanies = ["reliance", "national", "united india"];

    // Table configuration - no actions, no multi-select
    const tableConfig = {
        isAction: false,
        isEdit: false,
        isSearch: false,
        isViewURL: false,
        isSwitch: false,
        isDownload: false,
        isDelete: false,
        isMultiDelete: false,
        isCheckbox: false,
    };

    // Table columns
    const headCells = [
        {
            id: "companyName",
            placeMent: true,
            disablePadding: false,
            label: "Company Name",
        },
        // {
        //     id: "userName",
        //     placeMent: true,
        //     disablePadding: false,
        //     label: "Created By",
        // },
        {
            id: "dateFrom",
            placeMent: true,
            disablePadding: false,
            label: "From Date",
            type: "date",
        },
        {
            id: "dateTo",
            placeMent: true,
            disablePadding: false,
            label: "To Date",
            type: "date",
        },
        {
            id: "status",
            placeMent: true,
            disablePadding: false,
            label: "Status",
        },
        {
            id: "totalCount",
            placeMent: false,
            disablePadding: false,
            label: "Total Users",
        },
        {
            id: "finishedCount",
            placeMent: false,
            disablePadding: false,
            label: "Finished",
        },
        {
            id: "pendingCount",
            placeMent: false,
            disablePadding: false,
            label: "Pending",
        },
        {
            id: "errorCount",
            placeMent: false,
            disablePadding: false,
            label: "Errors",
        },
        {
            id: "createdAt",
            placeMent: false,
            disablePadding: false,
            label: "Created At",
            type: "date",
        },
    ];

    // Fetch companies for dropdown
    useEffect(() => {
        if (open) {
            fetchCompanies();
            fetchBulkUploadJobs();
        }
    }, [open]);

    const fetchCompanies = () => {
        GetCompany({ isAscending: true }).then((res) => {
            if (res.data) {
                // Filter companies based on allowed list
                const filtered = res.data.filter((company) => {
                    const companyName = company.shortName?.toLowerCase() || "";
                    return allowedCompanies.some((allowed) =>
                        companyName.includes(allowed)
                    );
                });
                setCompanyList(filtered);
            }
        });
    };

    const fetchBulkUploadJobs = () => {
        setLoading(true);

        GetBulkUploadJob({}).then((res) => {
            setLoading(false);
            console.log("Bulk Upload Jobs Response:", res);

            if (res.data) {
                console.log("Jobs data:", res.data);
                // Transform data for table
                const formattedData = res.data.map(job => {
                    // Format user name from populated userId
                    let userName = "-";
                    if (job.userId) {
                        if (typeof job.userId === 'object' && job.userId.firstName) {
                            userName = `${job.userId.firstName || ''} ${job.userId.lastName || ''}`.trim();
                        }
                    }

                    return {
                        ...job,
                        companyName: job.companyId?.shortName || job.companyName || "-",
                        userName: userName,
                    };
                });
                console.log("Formatted data:", formattedData);
                setBulkUploadJobs(Array.isArray(formattedData) ? formattedData : []);
            } else {
                console.log("No data in response");
                setBulkUploadJobs([]);
            }
        }).catch(err => {
            setLoading(false);
            console.error("Error fetching jobs:", err);
            setBulkUploadJobs([]);
        });
    };

    const handleSubmit = () => {
        if (!selectedCompany || !fromDate || !toDate) {
            return;
        }



        const userId = sessionStorage.getItem("UserId");

        if (!userId) {
            ToastError("User ID not found. Please login again.");
            return;
        }

        const payload = {
            companyId: selectedCompany._id,
            dateFrom: fromDate,
            dateTo: toDate,
            userId
        };

        PostBulkUploadJob(payload).then((res) => {
            if (!res.error) {
                fetchBulkUploadJobs();
                setSelectedCompany(null);
                setFromDate("");
                setToDate("");
            }
        });
    };

    const handleClose = () => {
        setSelectedCompany(null);
        setFromDate("");
        setToDate("");
        onClose();
    };

    const handleDownload = (row) => {
        console.log("Download requested for:", row);
        // Implement download logic here when API is ready
    };

    const handleMultiDelete = (rows) => {
        // Not used as delete is disabled, but required prop
    };

    // Get today's date for max date validation
    const today = new Date().toISOString().split("T")[0];

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="xl"
        >
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">Bulk Upload - Policy Verification</Typography>
                    <Box>
                        <IconButton
                            onClick={() => {
                                setRefreshing(true);
                                fetchBulkUploadJobs();
                                setTimeout(() => setRefreshing(false), 500);
                            }}
                            title="Refresh job list"
                            disabled={refreshing}
                        >
                            <RefreshIcon className={refreshing ? "rotating" : ""} />
                        </IconButton>
                        <IconButton onClick={handleClose}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </Box>
            </DialogTitle>

            <Divider />

            <DialogContent>
                {/* Filter Section with Border */}
                <Box
                    mb={3}
                    mt={1}
                    p={2}
                    sx={{
                        border: '2px solid #e0e0e0',
                        borderRadius: '8px',
                        backgroundColor: '#f9f9f9'
                    }}
                >
                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                        Create New Bulk Upload Job
                    </Typography>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={4}>
                            <Autocomplete
                                options={companyList}
                                getOptionLabel={(option) => option.shortName || ""}
                                value={selectedCompany}
                                onChange={(event, newValue) => setSelectedCompany(newValue)}
                                renderInput={(params) => (
                                    <TextField {...params} label="Select Company *" variant="outlined" size="small" fullWidth />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <TextField
                                label="From Date *"
                                type="date"
                                value={fromDate}
                                onChange={(e) => setFromDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ max: today }}
                                size="small"
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <TextField
                                label="To Date *"
                                type="date"
                                value={toDate}
                                onChange={(e) => setToDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                inputProps={{
                                    min: fromDate || undefined,
                                    max: today
                                }}
                                size="small"
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} sm={2}>
                            <Button
                                variant="contained"
                                color="primary"
                                fullWidth
                                onClick={handleSubmit}
                                disabled={!selectedCompany || !fromDate || !toDate}
                            >
                                Submit
                            </Button>
                        </Grid>
                    </Grid>
                </Box>

                {/* Jobs Table Section */}
                <Box>
                    <Typography variant="subtitle1" gutterBottom style={{ fontWeight: 600 }}>
                        Previous Bulk Upload Jobs
                    </Typography>

                    {/* Header Row */}
                    <Box
                        sx={{
                            mb: 1,
                            p: 1.5,
                            backgroundColor: '#f5f5f5',
                            borderRadius: '4px',
                            border: '1px solid #e0e0e0'
                        }}
                    >
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={2}>
                                <Typography variant="caption" fontWeight={700} color="text.secondary">
                                    COMPANY
                                </Typography>
                            </Grid>
                            <Grid item xs={2}>
                                <Typography variant="caption" fontWeight={700} color="text.secondary">
                                    DATE RANGE
                                </Typography>
                            </Grid>
                            <Grid item xs={1.5}>
                                <Typography variant="caption" fontWeight={700} color="text.secondary">
                                    STATUS
                                </Typography>
                            </Grid>
                            <Grid item xs={1}>
                                <Typography variant="caption" fontWeight={700} color="text.secondary">
                                    TOTAL
                                </Typography>
                            </Grid>
                            <Grid item xs={1}>
                                <Typography variant="caption" fontWeight={700} color="text.secondary">
                                    FINISHED
                                </Typography>
                            </Grid>
                            <Grid item xs={1}>
                                <Typography variant="caption" fontWeight={700} color="text.secondary">
                                    PENDING
                                </Typography>
                            </Grid>
                            <Grid item xs={1.5}>
                                <Typography variant="caption" fontWeight={700} color="text.secondary">
                                    ERRORS
                                </Typography>
                            </Grid>
                            <Grid item xs={2}>
                                <Typography variant="caption" fontWeight={700} color="text.secondary">
                                    CREATED AT
                                </Typography>
                            </Grid>
                        </Grid>
                    </Box>

                    {bulkUploadJobs.map((job, index) => (
                        <Box key={job._id || index} mb={2}>
                            <Box
                                sx={{
                                    border: '1px solid #e0e0e0',
                                    borderRadius: '8px',
                                    overflow: 'hidden',
                                    transition: 'box-shadow 0.2s',
                                    '&:hover': {
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                                    }
                                }}
                            >
                                {/* Job Row */}
                                <Box
                                    sx={{
                                        p: 2,
                                        backgroundColor: job.errorCount > 0 ? '#fafafa' : '#ffffff',
                                        cursor: job.errorCount > 0 ? 'pointer' : 'default',
                                        borderLeft: job.errorCount > 0 ? '4px solid #d32f2f' :
                                            job.status === 'running' ? '4px solid #1976d2' :
                                                job.status === 'complete' ? '4px solid #388e3c' :
                                                    '4px solid #757575',
                                        '&:hover': job.errorCount > 0 ? {
                                            backgroundColor: '#f5f5f5'
                                        } : {}
                                    }}
                                    onClick={() => {
                                        if (job.errorCount > 0) {
                                            const newExpanded = new Set(expandedRows);
                                            if (newExpanded.has(job._id)) {
                                                newExpanded.delete(job._id);
                                            } else {
                                                newExpanded.add(job._id);
                                            }
                                            setExpandedRows(newExpanded);
                                        }
                                    }}
                                >
                                    <Grid container spacing={2} alignItems="center">
                                        <Grid item xs={2}>
                                            <Typography variant="body2" fontWeight={600} color="text.primary">
                                                {job.companyName}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={2}>
                                            <Typography variant="caption" color="text.secondary">
                                                {new Date(job.dateFrom).toLocaleDateString()} - {new Date(job.dateTo).toLocaleDateString()}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={1.5}>
                                            <Box
                                                sx={{
                                                    px: 1.5,
                                                    py: 0.5,
                                                    borderRadius: '4px',
                                                    border: '1px solid',
                                                    borderColor:
                                                        job.status === 'complete' ? '#c8e6c9' :
                                                            job.status === 'running' ? '#bbdefb' :
                                                                job.status === 'error' ? '#ffcdd2' : '#e0e0e0',
                                                    backgroundColor:
                                                        job.status === 'complete' ? '#e8f5e9' :
                                                            job.status === 'running' ? '#e3f2fd' :
                                                                job.status === 'error' ? '#ffebee' : '#f5f5f5',
                                                    color:
                                                        job.status === 'complete' ? '#2e7d32' :
                                                            job.status === 'running' ? '#1565c0' :
                                                                job.status === 'error' ? '#c62828' : '#616161',
                                                    textAlign: 'center',
                                                    fontSize: '0.75rem',
                                                    fontWeight: 600,
                                                    textTransform: 'capitalize'
                                                }}
                                            >
                                                {job.status}
                                            </Box>
                                        </Grid>
                                        <Grid item xs={1}>
                                            <Typography variant="body2" color="text.secondary">
                                                Total: <strong>{job.totalCount}</strong>
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={1}>
                                            <Typography variant="body2" sx={{ color: '#2e7d32' }}>
                                                ✓ {job.finishedCount}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={1}>
                                            <Typography variant="body2" sx={{ color: '#f57c00' }}>
                                                ⏳ {job.pendingCount}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={1.5}>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: job.errorCount > 0 ? '#c62828' : '#757575',
                                                    fontWeight: job.errorCount > 0 ? 600 : 400
                                                }}
                                            >
                                                {job.errorCount > 0 ? '⚠' : '✓'} {job.errorCount}
                                                {job.errorCount > 0 && (
                                                    <Typography component="span" variant="caption" sx={{ ml: 0.5, fontSize: '0.7rem' }}>
                                                        (click)
                                                    </Typography>
                                                )}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={2}>
                                            <Typography variant="caption" color="text.secondary">
                                                {new Date(job.createdAt).toLocaleString()}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Box>

                                {/* Expandable Error Details */}
                                {job.errorCount > 0 && (
                                    <Collapse in={expandedRows.has(job._id)}>
                                        <Box sx={{ p: 2, backgroundColor: '#fafafa', borderTop: '1px solid #e0e0e0' }}>
                                            <Alert
                                                severity="error"
                                                sx={{
                                                    mb: 2,
                                                    backgroundColor: '#ffebee',
                                                    '& .MuiAlert-icon': {
                                                        color: '#c62828'
                                                    }
                                                }}
                                            >
                                                <AlertTitle sx={{ fontWeight: 600 }}>
                                                    Error Details ({job.errorCount} failed user{job.errorCount > 1 ? 's' : ''})
                                                </AlertTitle>
                                                {job.problems && job.problems.length > 0 ? (
                                                    <List dense>
                                                        {job.problems.map((problem, idx) => (
                                                            <ListItem
                                                                key={idx}
                                                                sx={{
                                                                    pl: 0,
                                                                    borderBottom: idx < job.problems.length - 1 ? '1px solid #ffcdd2' : 'none',
                                                                    pb: 1,
                                                                    mb: 1
                                                                }}
                                                            >
                                                                <ListItemText
                                                                    primary={
                                                                        <Typography variant="body2" fontWeight={600} color="text.primary">
                                                                            User: {problem.userId || 'Unknown'}
                                                                        </Typography>
                                                                    }
                                                                    secondary={
                                                                        <>
                                                                            <Typography variant="body2" color="error.main" sx={{ mt: 0.5 }}>
                                                                                {problem.error || 'No error message'}
                                                                            </Typography>
                                                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                                                                {problem.timestamp ? new Date(problem.timestamp).toLocaleString() : 'N/A'}
                                                                            </Typography>
                                                                        </>
                                                                    }
                                                                />
                                                            </ListItem>
                                                        ))}
                                                    </List>
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary">
                                                        No detailed error information available.
                                                    </Typography>
                                                )}
                                            </Alert>
                                        </Box>
                                    </Collapse>
                                )}
                            </Box>
                        </Box>
                    ))}

                    {bulkUploadJobs.length === 0 && !loading && (
                        <Box textAlign="center" py={4}>
                            <Typography variant="body2" color="textSecondary">
                                No bulk upload jobs found. Create one above to get started.
                            </Typography>
                        </Box>
                    )}
                </Box>

                {/* Add CSS for rotating animation */}
                <style>{`
                    @keyframes rotate {
                        from { transform: rotate(0deg); }
                        to { transform: rotate(360deg); }
                    }
                    .rotating {
                        animation: rotate 1s linear infinite;
                    }
                `}</style>
            </DialogContent>
        </Dialog>
    );
};

export default BulkUploadDialog;
